import { Bell, Search, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// In-memory cache so we don't refetch player names on every route change.
const playerNameCache = new Map();

function usePlayerName(playerId) {
  const [name, setName] = useState(() => (playerId ? playerNameCache.get(playerId) || null : null));
  useEffect(() => {
    if (!playerId) { setName(null); return; }
    const cached = playerNameCache.get(playerId);
    if (cached) { setName(cached); return; }
    let cancelled = false;
    api.get(`/players/${playerId}`).then((r) => {
      if (cancelled) return;
      playerNameCache.set(playerId, r.data.full_name);
      setName(r.data.full_name);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [playerId]);
  return name;
}

function Crumbs() {
  const loc = useLocation();
  const parts = loc.pathname.split("/").filter(Boolean);
  // Detect "/app/players/:uuid/..." pattern
  const playerIdIdx = parts.findIndex((p) => UUID_RE.test(p));
  const playerId = playerIdIdx > -1 ? parts[playerIdIdx] : null;
  const playerName = usePlayerName(playerId);

  return (
    <div className="flex items-center gap-1.5 text-2 text-sm" data-testid="top-header-breadcrumb">
      <span className="text-3">OmniDraftLab</span>
      {parts.map((p, i) => {
        const isLast = i === parts.length - 1;
        const isUuid = UUID_RE.test(p);
        const label = isUuid ? (playerName || "Player") : p.replace(/-/g, " ");
        return (
          <span key={i} className="flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5 text-3" />
            <span
              className={isLast ? "text-1 capitalize" : "capitalize text-2"}
              data-testid={isUuid ? "breadcrumb-player-name" : undefined}
            >
              {label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

export function TopHeader({ onMobileMenu }) {
  const { user, logout } = useAuth();
  return (
    <header
      data-testid="app-top-header"
      className="sticky top-0 z-20 h-16 flex items-center gap-4 border-b border-white/10 bg-white/[0.04] backdrop-blur-2xl px-4 sm:px-6"
    >
      <button
        onClick={onMobileMenu}
        className="lg:hidden text-white/70 hover:text-white"
        data-testid="top-header-mobile-menu"
        aria-label="Open menu"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="4" y1="6" x2="20" y2="6" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="18" x2="20" y2="18" />
        </svg>
      </button>

      <Crumbs />

      <div className="ml-auto flex items-center gap-3">
        <div className="relative hidden md:block">
          <Search className="h-4 w-4 text-3 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="top-header-search-input"
            placeholder="Search players, reports…"
            className="pl-9 w-[260px] bg-white/5 border-white/10 text-1 placeholder:text-3 focus-visible:ring-cyan-300/40"
          />
        </div>
        <button
          data-testid="top-header-notifications-button"
          className="relative h-9 w-9 grid place-items-center rounded-xl bg-white/5 border border-white/10 text-white/80 hover:text-white"
        >
          <Bell className="h-4 w-4" />
          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-cyan-300" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="top-header-user-menu" className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-2 py-1.5 hover:bg-white/[0.08]">
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-gradient-to-br from-cyan-400 to-blue-500 text-[#04111A] text-xs font-semibold">
                  {(user?.full_name || "?").slice(0, 1)}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left leading-tight">
                <div className="text-1 text-xs font-medium">{user?.full_name}</div>
                <div className="text-3 text-[10px] uppercase tracking-wider">{user?.role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="glass-strong border-white/10">
            <DropdownMenuLabel className="text-2">Signed in as</DropdownMenuLabel>
            <DropdownMenuItem disabled className="text-1">{user?.email}</DropdownMenuItem>
            <DropdownMenuSeparator className="bg-white/10" />
            <DropdownMenuItem data-testid="top-header-logout" onClick={logout} className="text-rose-300">Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
